import { button, divider } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { toNumber } from "@/utils/number";
import { Box, Stack, css, styled } from "@mui/material";
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";

export type TMenuContent = {
  title: string;
  content: ReactNode;
};

type TMenuNavigateProps = {
  contents: TMenuContent[];
  menuPrefix?: ReactNode;
  menuAffix?: ReactNode;
  contentPrefix?: ReactNode;
  contentAffix?: ReactNode;
  bottomBtn?: ReactNode;
  stickyTop?: number;
  paddingBottom?: number;
  contentWidth?: string;
  // When set, on mount auto-scroll once to the section whose title matches (used by deep links with a URL hash).
  scrollToTitle?: string;
};

export const MenuNavigate: FC<TMenuNavigateProps> = ({
  contents,
  menuPrefix,
  menuAffix,
  contentPrefix,
  contentAffix,
  stickyTop = 80,
  paddingBottom = 0,
  contentWidth,
  scrollToTitle,
}) => {
  const refs = useRef<HTMLDivElement[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const disabledScrollFn = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hasAutoScrolledRef = useRef(false);
  const scrollTargetY = useRef(0);

  // Fired by "scrollend" once the programmatic smooth scroll settles — re-enable the scroll-spy.
  const enableScrollSpy = useCallback(() => {
    disabledScrollFn.current = false;
  }, []);

  // Smooth-scroll the window to the section box at `index`, offsetting by `stickyTop` so the section
  // lands just below the sticky side menu. Also marks that item active and temporarily disables the
  // scroll-spy (disabledScrollFn) so it doesn't fight this programmatic scroll. Shared by menu clicks
  // and the deep-link auto-scroll effect.
  const scrollToIndex = useCallback(
    (index: number) => {
      if (!refs.current[index]) return;
      const position = refs.current[index].getBoundingClientRect();
      // Clamp to the scrollable range so sections near the page bottom keep a reachable target.
      const maxScrollY = document.documentElement.scrollHeight - window.innerHeight;
      const targetTop = Math.min(Math.max(position.top + window.scrollY - stickyTop, 0), maxScrollY);
      setActiveIndex(index);
      // Already at target: the smooth scroll emits no events (and no "scrollend"), so keep the spy on.
      if (Math.abs(window.scrollY - targetTop) < 1) return;
      disabledScrollFn.current = true;
      scrollTargetY.current = targetTop;
      window.addEventListener("scrollend", enableScrollSpy, { once: true });
      window.scrollTo({ behavior: "smooth", left: position.left, top: targetTop });
    },
    [stickyTop, enableScrollSpy],
  );

  const handleClickMenuItem = (index: number) => {
    scrollToIndex(index);
  };

  // Deep-link support: scroll once to the section matching `scrollToTitle`. Wait for web fonts to
  // finish loading (they shift section heights) instead of a fixed timeout — resolves immediately
  // when fonts are already cached.
  useEffect(() => {
    if (!scrollToTitle || hasAutoScrolledRef.current) return;
    const index = contents.findIndex((c) => c.title === scrollToTitle);
    if (index < 0) return;
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (cancelled || hasAutoScrolledRef.current || !refs.current[index]) return;
      scrollToIndex(index);
      hasAutoScrolledRef.current = true;
    });
    return () => {
      cancelled = true;
    };
  }, [scrollToTitle, contents, scrollToIndex]);

  useEffect(() => {
    const handleScroll = () => {
      if (disabledScrollFn.current) {
        // Programmatic scroll in progress. Fallback for browsers without "scrollend"
        // (Safari < 18.4): re-enable the spy once the scroll reaches its target.
        if (Math.abs(window.scrollY - scrollTargetY.current) < 2) disabledScrollFn.current = false;
        return;
      }
      const isHalfBottom = window.scrollY > toNumber(containerRef.current?.clientHeight) / 3;

      refs.current.forEach((ref, index) => {
        if (!ref) return;
        const rect = ref.getBoundingClientRect();
        if (rect.top - window.innerHeight + (window.innerHeight * (isHalfBottom ? 2 : 5)) / 7 < 0) setActiveIndex(index);
      });
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scrollend", enableScrollSpy);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [enableScrollSpy]);

  return (
    <MenuNavigateWrap ref={containerRef} paddingBottom={paddingBottom}>
      <Menu stickyTop={stickyTop}>
        {menuPrefix}
        {contents.map(({ title }, index) => (
          <MenuItem active={activeIndex === index} key={index} onClick={() => handleClickMenuItem(index)}>
            {title}
          </MenuItem>
        ))}
        {menuAffix}
      </Menu>
      <Stack
        gap={4}
        flex={1}
        {...(contentWidth && {
          sx: {
            maxWidth: contentWidth,
          },
        })}
      >
        {contentPrefix}
        {contents.map(({ content }, index) => (
          <Box key={index} ref={(ref: HTMLDivElement) => (refs.current[index] = ref)}>
            {content}
          </Box>
        ))}
        {contentAffix}
      </Stack>
    </MenuNavigateWrap>
  );
};

const options = { shouldForwardProp: (propName: string) => !["active", "stickyTop"].includes(propName) };

const MenuNavigateWrap = styled(Stack)`
  flex-direction: row;
  gap: 24px;
  display: flex;
  justify-content: space-between;
`;

const Menu = styled(Stack, options)<{ stickyTop: number }>`
  gap: 8px;
  min-width: 200px;
  position: sticky;
  ${({ stickyTop }) => `top: ${stickyTop}px`};
  height: fit-content;
`;

const MenuItem = styled(Box, options)<{ active: boolean }>`
  position: relative;
  border-left: 4px solid ${divider.middle};
  padding: 8px 16px;
  padding-left: 12px;
  cursor: pointer;
  max-width: 210px;
  ${css(typography.body14)};
  ${({ active }) => active && MenuItemActive};
`;

const MenuItemActive = css`
  border-color: ${button.primary};
  pointer-events: none;
`;
